import React, {Fragment, useEffect, useMemo, useState} from "react";
import {Listbox, Transition} from "@headlessui/react"

interface DropdownListItem {
    name: number | string,
    id: any
}

interface IProps {
    title?: string,
    placeholder?: string,
    id: any,
    data: Array<DropdownListItem>,
    errMsg?: string,
    onChange: (id: any) => void
}

const List = (props: IProps) => {
    const {id, data, onChange} = props

    const item = data.find((_item) => {
        return _item.id === id
    })

    return (
        <div>
            <div className={"relative w-full"}>
                <Listbox value={id} onChange={(val) => {
                    onChange && onChange(val)
                }}>
                    {({open}) => {
                        return (
                            <div className="w-full">
                                <Listbox.Button className={`w-full px-2 py-2 flex flex-row justify-between items-center rounded-lg focus:outline-none ${open ? "outline-none ring-2 ring-theme-blue" : ""}`}>
                                    <p className={"text-sm text-center"}>{item?.name || id}</p>
                                </Listbox.Button>
                                <Transition
                                    as={Fragment}
                                    enter="transition duration-100 ease-out"
                                    enterFrom="transform scale-95 opacity-0"
                                    enterTo="transform scale-100 opacity-100"
                                    leave="transition duration-75 ease-out"
                                    leaveFrom="transform scale-100 opacity-100"
                                    leaveTo="transform scale-95 opacity-0"
                                >
                                    <Listbox.Options static className="z-30 bg-white absolute w-16 py-1 mt-1 overflow-auto text-base bg-white rounded-lg shadow-2xl border max-h-60 focus:outline-none">
                                        {data.map((item, idx) => {
                                            const title = item.name
                                            return (
                                                <Listbox.Option
                                                    key={idx}
                                                    value={item.id}
                                                    className={"px-1"}
                                                >
                                                    {({active }) => {
                                                        return (
                                                            <div
                                                                className={`${
                                                                    active ? "bg-theme-blue-10 text-theme-blue font-semibold" : "bg-white text-gray-1 font-normal"
                                                                } w-full py-2 px-2 text-sm rounded`}
                                                            >
                                                                {/* {selected && <i className="fas fa-check mr-4"></i>}*/}
                                                                {title}
                                                            </div>
                                                        )
                                                    }}
                                                </Listbox.Option>
                                            )
                                        })}
                                    </Listbox.Options>
                                </Transition>
                            </div>
                        )
                    }}
                </Listbox>
            </div>
        </div>
    )
}

interface IDateTimeProps {
    dateTime: string,
    onChangeValue: (dateTime: string) => void
}
export default function DateTimePicker(props: IDateTimeProps) {
    // const dataForHour: Array<DropdownListItem> = [
    //     {name: "00", id: "00"},
    //     {name: "01", id: "01"},
    //     {name: "02", id: "02"},
    //     {name: "03", id: "03"},
    //     {name: "04", id: "04"},
    //     {name: "05", id: "05"},
    //     {name: "06", id: "06"},
    //     {name: "07", id: "07"},
    //     {name: "08", id: "08"},
    //     {name: "09", id: "09"},
    //     {name: "10", id: "10"},
    //     {name: "11", id: "11"},
    //     {name: "12", id: "12"},
    // ]
    const dataForMin: Array<DropdownListItem> = [
        {name: "00", id: "00"},
        {name: "15", id: "15"},
        {name: "30", id: "30"},
        {name: "45", id: "45"},
    ]
    const dataForAPMPM: Array<DropdownListItem> = [
        {name: "AM", id: "AM"},
        {name: "PM", id: "PM"},
    ]

    const {dateTime, onChangeValue} = props
    if (dateTime.length !== 5) {
        return null
    }

    const convert24To12 = (time24: string): string => {
        const timeInt = parseInt(time24)
        if (timeInt < 12) {
            return time24
        } else if (timeInt >= 12 && timeInt < 22) {
            return `0${timeInt - 12}`
        }
        return `${timeInt - 12}`
    }

    const convert12To24 = (time12: string, min: string, isAm: boolean): string => {
        const timeInt12 = parseInt(time12)
        if (isAm) {
            return `${time12}:${min}`
        }
        return `${timeInt12 + 12}:${min}`
    }

    const hour24 = dateTime.slice(0, 2)
    const minDefault = dateTime.slice(3)
    const apmDefault = parseInt(hour24) < 12 ? "AM" : "PM"
    const [hour, setHour] = useState<string>(convert24To12(hour24))
    const [min, setMin] = useState<string>(minDefault)
    const [apm, setApm] = useState<string>(apmDefault)

    useEffect(() => {
        setHour(convert24To12(hour24))
        setMin(minDefault)
        setApm(apmDefault)
    }, [props.dateTime])

    useEffect(() => {
        const dateTime = convert12To24(hour, min, apm === "AM")
        onChangeValue && onChangeValue(dateTime)
    }, [min])

    useEffect(() => {
        let _hour = hour
        let _min = min
        if ((apm === "AM" && parseInt(_hour) < 8)
        ) {
            _hour = "08"
            _min = "00"
        }

        if ((apm === "PM" && parseInt(_hour) >= 9)) {
            _hour = "09"
            _min = "00"
        }

        const dateTime = convert12To24(_hour, _min, apm === "AM")
        onChangeValue && onChangeValue(dateTime)
    }, [apm])

    useEffect(() => {
        let _min = min
        if (hour === "09" && apm === "PM") {
            _min = "00"
        }
        const dateTime = convert12To24(hour, _min, apm === "AM")
        onChangeValue && onChangeValue(dateTime)
    }, [hour])

    const onChangeHour = (id: string) => {
        setHour(id)
    }

    const dataSourceForHour = () => {
        if (apm === "PM") {
            return [
                {name: "12", id: "00"},
                {name: "01", id: "01"},
                {name: "02", id: "02"},
                {name: "03", id: "03"},
                {name: "04", id: "04"},
                {name: "05", id: "05"},
                {name: "06", id: "06"},
                {name: "07", id: "07"},
                {name: "08", id: "08"},
                {name: "09", id: "09"},
            ]
        }
        return [
            {name: "08", id: "08"},
            {name: "09", id: "09"},
            {name: "10", id: "10"},
            {name: "11", id: "11"},
        ]
    }
    const $hourList = (
        <List id={hour} data={dataSourceForHour()} onChange={onChangeHour} />
    )
    const onChangeMin = (id: string) => {
        setMin(id)
    }

    const dataSourceForMin = () => {
        if (apm === "PM" && hour === "09") {
            return [{name: "00", id: "00"}]
        }
        return dataForMin
    }
    const $minList = (
        <List id={min} data={dataSourceForMin()} onChange={onChangeMin} />
    )
    const onChangeAPM = (id: string) => {
        setApm(id)
    }

    const apmDataSource = useMemo(() => {
        if (hour === "12") {
            setApm("PM")
            return dataForAPMPM.slice(1, 1)
        }
        return dataForAPMPM
    }, [hour])

    const $apmList = (
        <List id={apm} data={apmDataSource} onChange={onChangeAPM} />
    )
    const $dot = (
        <span className={"mb-1 mx-0.5"}>:</span>
    )
    return (
        <div className="flex flex-row items-center">
            {$hourList}
            {$dot}
            {$minList}
            {$apmList}
        </div>
    )
}
